import { useState, useEffect } from "react";
import { Table, Pagination, IconButton } from "rsuite";
import CollaspedOutlineIcon from "@rsuite/icons/CollaspedOutline";
import ExpandOutlineIcon from "@rsuite/icons/ExpandOutline";
import FilterableHeaderCell from "../filterable-header-cell";
import "./index.css";
function DataTable({
  data,
  columns,
  sortColumn,
  sortType,
  onSortColumn,
  loading,
  activeFilters,
  onFilterClick,
  expandable = false,
  renderRowExpanded,
  rowExpandedHeight = 600,
  onRowClick,
  rowClassName,
  showRowCount = true,
  rowKey = "id",
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  limitOptions = [10, 30, 50],
  footer,
  ...tableProps
}) {
  const { Column, Cell, HeaderCell } = Table;
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  // Use pagination from props if provided, otherwise use local state
  const [localActivePage, setLocalActivePage] = useState(1);

  const activePage = currentPage || localActivePage;
  const limit = pageSize || 10;
  const total = totalItems || (data?.length || 0);

  // Use provided data directly if pagination is handled by the hook
  const pageData = currentPage ? (data || []) : (data || []).slice((activePage - 1) * limit, activePage * limit);  const handleExpanded = (rowData) => {
    if (expandedRowKeys.includes(rowData?.[rowKey])) {
      setExpandedRowKeys([]);
      return;
    }
    setExpandedRowKeys([rowData?.[rowKey]]);
  };
  const ExpandCell = ({ rowData, ...props }) => (
    <Cell {...props} style={{ padding: 5 }}>
      <IconButton
        appearance="subtle"
        onClick={() => handleExpanded(rowData)}
        icon={expandedRowKeys.includes(rowData?.[rowKey]) ? <CollaspedOutlineIcon /> : <ExpandOutlineIcon />}
      />
    </Cell>
  );  
  const getRowClassName = (rowData) => {
    let className = "";
    if (expandable && expandedRowKeys.includes(rowData?.[rowKey])) {
      className += "expanded-row ";
    }
    if (rowClassName) {
      className += typeof rowClassName === "function" ? rowClassName(rowData) : rowClassName;
    }
    return className.trim();
  };

  useEffect(() => {
    if (!currentPage) {
      setLocalActivePage(1);
    }
  }, [data, currentPage]);

  return (
    <>
      <Table
        fillHeight
        data={pageData}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={onSortColumn}
        loading={loading}
        hover={true}
        autoHeight={false}
        wordWrap={true}
        virtualized={false}
        expandedRowKeys={expandable ? expandedRowKeys : undefined}
        renderRowExpanded={expandable ? renderRowExpanded : undefined}
        rowKey={rowKey}
        rowExpandedHeight={expandable ? rowExpandedHeight : undefined}
        rowClassName={getRowClassName}
        onRowClick={onRowClick}
        {...tableProps}
      >
        {expandable && (
          <Column width={50} align="center" fixed>
            <HeaderCell />
            <ExpandCell />
          </Column>)}{" "}
        {(columns || []).map((column, index) => (
          <Column
            key={column.key || index}
            width={column.width}
            flexGrow={column.flexGrow}
            resizable={column.resizable !== false}
            sortable={column.sortable !== false}
            fixed={column.fixed}
            align={column.align}
            dataKey={column.dataKey}
          >
            {column.filterable ? (
              <FilterableHeaderCell
                columnKey={column.dataKey}
                hasActiveFilters={activeFilters && activeFilters[column.dataKey]}
                onFilterClick={onFilterClick}
              >
                {column.title}
              </FilterableHeaderCell>
            ) : (
              <HeaderCell>{column.title}</HeaderCell>
            )}

            {column.render ? <Cell dataKey={column.dataKey}>{(rowData, rowIndex) => column.render(rowData, rowIndex)}</Cell> : <Cell dataKey={column.dataKey} />}
          </Column>
        ))}
      </Table>
      <div style={{ 
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px'
      }}>
        <div data-testid="footer">{footer}</div>
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="sm"
          layout={["-", "limit", "|", "pager", "skip"]}
          total={total}
          limitOptions={limitOptions}
          limit={limit}
          activePage={activePage}
          onChangePage={onPageChange}
          onChangeLimit={onPageSizeChange}
        />
      </div>
    </>
  );
}

export default DataTable;
